import type { Company, TagTone } from '@/constants/companies'

export const DEAL_STAGES = [
    { value: 'Discovery', probability: 10, category: 'pipeline', tone: 'blue' },
    { value: 'Qualification', probability: 25, category: 'pipeline', tone: 'purple' },
    { value: 'Proposal', probability: 50, category: 'bestCase', tone: 'amber' },
    { value: 'Negotiation', probability: 75, category: 'commit', tone: 'orange' },
    { value: 'Closed Won', probability: 100, category: 'won', tone: 'green' },
    { value: 'Closed Lost', probability: 0, category: 'lost', tone: 'red' }
] as const satisfies ReadonlyArray<{ value: string, probability: number, category: string, tone: TagTone }>

export type DealStage = (typeof DEAL_STAGES)[number]['value']
export type ForecastCategory = (typeof DEAL_STAGES)[number]['category']

export const CLOSED_STAGES: string[] = ['Closed Won', 'Closed Lost']

export function stageMeta(stage: string) {
    return DEAL_STAGES.find(item => item.value === stage) ?? DEAL_STAGES[0]
}

export const PIPELINES = [
    { slug: 'north-america', label: "North America", dot: 'bg-[#ffdb4b]' },
    { slug: 'emea-enterprise', label: "EMEA Enterprise", dot: 'bg-[#fe4a8e]' },
    { slug: 'apac-expansion', label: "APAC Expansion", dot: 'bg-[#9668fe]' }
] as const

export type PipelineSlug = (typeof PIPELINES)[number]['slug']

export function pipelineBySlug(slug: string) {
    return PIPELINES.find(item => item.slug === slug) ?? null
}

export const ACTIVITY_TYPES = ['Call', 'Email', 'Meeting', 'Task', 'Note'] as const

export type ActivityType = (typeof ACTIVITY_TYPES)[number]

export const SEQUENCE_STATUSES = [
    { value: 'Active', tone: 'green' },
    { value: 'Paused', tone: 'amber' },
    { value: 'Draft', tone: 'neutral' }
] as const satisfies ReadonlyArray<{ value: string, tone: TagTone }>

export type SequenceStatus = (typeof SEQUENCE_STATUSES)[number]['value']

export function sequenceStatusMeta(status: string) {
    return SEQUENCE_STATUSES.find(item => item.value === status) ?? SEQUENCE_STATUSES[2]
}

export const SEQUENCE_CHANNELS = ['Email', 'Call', 'Task'] as const

export type SequenceChannel = (typeof SEQUENCE_CHANNELS)[number]

export const SUPPORT_TOPICS = [
    "Using the CRM",
    "Data or import question",
    "Something is not working",
    "Billing and plans",
    "Other"
] as const

export const QUARTERLY_TARGET = 1500000

export type Deal = {
    id: string
    name: string
    companyId: string | null
    owner: string
    stage: string
    pipeline: string
    value: number
    closeDate: string
    nextStep: string
}

export type Contact = {
    id: string
    name: string
    title: string
    email: string
    phone: string
    companyId: string | null
    owner: string
    lastContacted: string | null
}

export type Activity = {
    id: string
    type: string
    subject: string
    notes: string
    companyId: string | null
    contactId: string | null
    owner: string
    dueDate: string
    completed: boolean
}

export type SequenceStep = {
    day: number
    channel: string
    subject: string
}

export type Sequence = {
    id: string
    name: string
    description: string
    owner: string
    status: string
    steps: SequenceStep[]
    enrolled: number
    openRate: number
    replyRate: number
}

export type CrmData = {
    companies: Company[]
    deals: Deal[]
    contacts: Contact[]
    activities: Activity[]
    sequences: Sequence[]
}

export type Team = {
    slug: string
    name: string
    description: string
    members: string[]
}

export const TEAMS: Team[] = [
    {
        slug: 'strategic-aes',
        name: "Strategic AEs",
        description: "Owns the largest enterprise and strategic accounts.",
        members: ['Sarah Nguyen', 'James Taylor', 'Maria Keller', 'Alex Santos', 'Mark Darnalds', 'Grace Miller']
    },
    {
        slug: 'mid-market',
        name: "Mid Market",
        description: "Grows mid-market accounts through expansion and co-sell.",
        members: ['Lina Wong', 'Jamie Fox', 'Ricky Brown', 'Ava Brooks', 'Hannah Mills', 'Emma Green']
    },
    {
        slug: 'sdr-team',
        name: "SDR Team",
        description: "Books first meetings and qualifies new pipeline.",
        members: ['Nia Jameson', 'Drew Nash', 'Kate Chen', 'Oliver Chan', 'Noah Lee', 'Chloe Park']
    }
]

export function teamBySlug(slug: string): Team | null {
    return TEAMS.find(team => team.slug === slug) ?? null
}
